import React from "react";

/**
 * Mission & Vision — design/stitch/our-story.html §4
 * Cloud Mist band + white organic wave at top (into founder white above).
 */
const Mission = () => {
  return (
    <section
      className="py-14 sm:py-20 relative overflow-hidden text-left"
      style={{
        /* Stitch: mist wash */
        backgroundColor:
          "color-mix(in srgb, var(--color-cloud-mist) 50%, var(--color-white))",
      }}
    >
      {/* Organic Top Wave Divider — white fill into mist */}
      <div
        className="absolute top-0 left-0 right-0 w-full overflow-hidden leading-none pointer-events-none"
        aria-hidden="true"
      >
        <svg
          className="w-full block h-8 sm:h-10 md:h-12"
          fill="none"
          preserveAspectRatio="none"
          viewBox="0 0 1440 48"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M0 0C480 36 960 36 1440 0V48H0V0Z"
            fill="var(--color-white)"
          />
        </svg>
      </div>

      <div className="max-w-7xl mx-auto px-5 sm:px-6 md:px-10 relative z-10 pt-6 sm:pt-8 md:pt-10">
        <div className="max-w-2xl mb-8 sm:mb-12">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-surface-white text-sky-blue font-headline text-xs font-semibold mb-3 shadow-sm">
            <svg
              aria-hidden="true"
              fill="none"
              viewBox="0 0 24 24"
              width="16"
              height="16"
              stroke="currentColor"
              strokeWidth="2"
            >
              <circle cx="12" cy="12" r="3" />
              <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" />
            </svg>
            <span>Guiding Principles</span>
          </div>
          <h2 className="font-headline font-extrabold text-2xl sm:text-3xl lg:text-4xl text-ink tracking-tight">
            Our Purpose & Horizon
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 sm:gap-8">
          <article className="bg-surface-white rounded-brand p-6 sm:p-8 md:p-10 border border-hairline shadow-brand-sm relative flex flex-col justify-between hover:shadow-brand-md transition-shadow">
            <div
              className="absolute top-5 right-5 sm:top-6 sm:right-6 w-10 h-10 sm:w-12 sm:h-12 rounded-brand flex items-center justify-center text-spectrum-blue"
              style={{
                backgroundColor:
                  "color-mix(in srgb, var(--color-cloud-mist) 50%, var(--color-white))",
              }}
              aria-hidden="true"
            >
              <svg
                fill="none"
                viewBox="0 0 24 24"
                width="22"
                height="22"
                stroke="currentColor"
                strokeWidth="1.75"
              >
                <path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z" />
              </svg>
            </div>
            <div className="pr-12 sm:pr-14">
              <span className="inline-block px-3 py-1 rounded-md bg-cloud-mist text-ink font-headline text-xs font-semibold uppercase tracking-wider mb-4">
                Mission
              </span>
              <h3 className="font-headline font-bold text-xl md:text-2xl text-ink mb-4">
                Transforming Journeys
              </h3>
              <p className="font-body text-base text-muted leading-relaxed">
                To empower parents and caregivers navigating neurodivergence with
                an innovative platform that combines expert insights, cutting-edge
                research, and compassionate, nonjudgmental community
                support—transforming isolation into connection, uncertainty into
                confidence, and individual challenges into collective resilience.
              </p>
            </div>
            <div className="mt-8 pt-4 border-t border-cloud-mist flex items-center gap-2 text-ink font-headline text-sm font-semibold">
              <span
                className="w-2 h-2 rounded-full bg-sky-blue shrink-0"
                aria-hidden="true"
              />
              <span>Empowerment through shared lived experience</span>
            </div>
          </article>

          <article className="bg-surface-white rounded-brand p-6 sm:p-8 md:p-10 border border-hairline shadow-brand-sm relative flex flex-col justify-between hover:shadow-brand-md transition-shadow">
            <div
              className="absolute top-5 right-5 sm:top-6 sm:right-6 w-10 h-10 sm:w-12 sm:h-12 rounded-brand flex items-center justify-center text-spectrum-blue"
              style={{
                backgroundColor:
                  "color-mix(in srgb, var(--color-cloud-mist) 50%, var(--color-white))",
              }}
              aria-hidden="true"
            >
              <svg
                fill="none"
                viewBox="0 0 24 24"
                width="22"
                height="22"
                stroke="currentColor"
                strokeWidth="1.75"
              >
                <circle cx="12" cy="12" r="3" />
                <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12z" />
              </svg>
            </div>
            <div className="pr-12 sm:pr-14">
              <span className="inline-block px-3 py-1 rounded-md bg-cloud-mist text-spectrum-blue font-headline text-xs font-semibold uppercase tracking-wider mb-4">
                Vision
              </span>
              <h3 className="font-headline font-bold text-xl md:text-2xl text-ink mb-4">
                A Celebrated World
              </h3>
              <p className="font-body text-base text-muted leading-relaxed">
                To create a world where families navigating neurodivergence feel
                seen, supported, and celebrated for their unique
                journeys—fostering inclusive, empowered communities built on
                understanding, connection, and shared growth.
              </p>
            </div>
            <div className="mt-8 pt-4 border-t border-cloud-mist flex items-center gap-2 text-ink font-headline text-sm font-semibold">
              <span
                className="w-2 h-2 rounded-full bg-sunshine shrink-0"
                aria-hidden="true"
              />
              <span>Inclusion without borders or stigma</span>
            </div>
          </article>
        </div>
      </div>
    </section>
  );
};

export default Mission;
